import { useId } from "react";
import type { KeyboardEvent, ReactElement } from "react";
import type { TicketKarte, Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Chip } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { berlinerTag, datumsMarke, tageZwischen } from "./ticketFormat.js";
import { Avatar } from "./Avatar.js";

/**
 * DIE TICKET-KARTE (Spec „Ticket-Karte"): Objektzeilen-Optik als Übergang T-1 (me-objektzeile
 * trägt die Board-Karte nicht), Art-Symbol zuerst, der Zuständige als Avatar mit Namen (F48,
 * me-avatar). Titel, Namen und Stand aus Fremdsystemen sind reiner Text (React escaped,
 * kein rohes HTML).
 *
 * Interaktion ohne Verschachtelung (Review 12): Die Karte selbst ist ein nicht-interaktiver
 * `article` (Name aus Nummer und Titel, Beschreibung aus Datum und Zuständigem). Nummer und
 * Titel SIND der Öffnen-Knopf; sein Pseudo-Element deckt die ganze Karte (Klick irgendwo
 * öffnet), Griff, Avatar, Kunden-Chip und „Verschieben …" (Symbolknopf rechts unten) liegen
 * per z-index darüber und handeln selbst. Der Griff ist nur für den Zeiger da (`aria-hidden`); Tastatur und Telefon
 * nehmen „Verschieben …" (F22).
 */

const SYSTEM: Record<string, string> = { GITLAB: "GitLab", YOUTRACK: "YouTrack", URL: "Link" };
/** Art des Tickets mit Symbol — eine Zuordnung für Karte und Arbeitsdialog (TicketDialog.tsx). */
export const ART: Record<TicketKarte["kind"], { symbol: string; name: string }> = {
  TASK: { symbol: "square-check", name: "Aufgabe" },
};

export type TicketKarteProps = {
  t: TicketKarte; heute: string; ichId: string; companies: readonly Unternehmen[];
  onOeffnen: (id: string) => void; onVerschieben: (t: TicketKarte) => void;
  griff?: Record<string, unknown>; zieht?: boolean;
};

export function TicketKarteAnsicht(p: TicketKarteProps): ReactElement {
  const t = p.t;
  const id = useId();
  const marke = datumsMarke(t.resubmitDate, p.heute);
  const erster = t.customers[0];
  const kundenPopup = useSofortPopup<HTMLSpanElement>(
    t.customers.length === 0 ? "" : `${t.companyShortCode} · ${t.customers.map((c) => c.name).join(", ")}`);
  const verschiebenPopup = useSofortPopup<HTMLSpanElement>("Verschieben …");
  const tasten = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); p.onOeffnen(t.id); }
  };
  const art = ART[t.kind];
  const link = t.firstLink;
  return (
    <article className={`tk${p.zieht === true ? " tk--zieht" : ""}`} data-ticket={t.id}
      aria-labelledby={`${id}-oeffnen`} aria-describedby={`${id}-datum ${id}-wer`}>
      {/* Der Griff ⠿ (Übergang T-2): gezogen wird NUR von hier (Zeiger), nie von der ganzen
          Karte — so bleibt am Telefon das Rollen frei. Kein Tabstopp, für die Vorlesestimme
          stumm: deren Weg ist „Verschieben …" (F22). */}
      <span className="tk__griff tk__oben" aria-hidden="true" {...(p.griff ?? {})}>⠿</span>
      <div className="tk__kopf">
        <Icon name={art.symbol} label={art.name} className="tk__art" />
        <span id={`${id}-oeffnen`} className="tk__oeffnen" role="button" tabIndex={0}
          onClick={() => p.onOeffnen(t.id)} onKeyDown={tasten}>
          <span className="tk__nummer">{t.ticketNumber}</span>{" "}
          <span className="tk__titel">{t.title}</span>
        </span>
        {/* Das Datum rechts neben dem Titel (Entwurf v001): der Titel bekommt die Breite. */}
        <span id={`${id}-datum`} className="tk__datumfeld">
          {marke.art === null
            ? <Chip className={`tk__datum${marke.ohne ? " tk__datum--ohne" : ""}`}>{marke.text}</Chip>
            : <Chip className="tk__datum" statusArt={marke.art}>{marke.text}</Chip>}
        </span>
      </div>
      <div className="tk__zeile">
        {/* Über dem Öffnen-Knopf, damit das Popup mit allen Kunden erscheint; ein Klick öffnet
            trotzdem (nur Zeiger — die Tastatur öffnet über Nummer und Titel). */}
        <span ref={kundenPopup} className="tk__kundefeld tk__oben" onClick={() => p.onOeffnen(t.id)}>
          <Chip className="tk__kunde" {...hausfarbeChip(p.companies, t.companyShortCode)}>
            <span className="tk__kundetext">
              {erster === undefined ? t.companyShortCode : `${t.companyShortCode} · ${erster.name}`}
            </span>
          </Chip>
        </span>
        {t.customers.length > 1 && <span className="tk__mehr">+{t.customers.length - 1}</span>}
        {t.contacts[0] !== undefined && <span className="tk__neben tk__kontakt">{t.contacts[0].name}</span>}
      </div>
      <div className="tk__fuss">
        {/* Antippen zeigt den Steckbrief (F48) und öffnet NICHT das Ticket. */}
        <span id={`${id}-wer`} className="tk__wer tk__oben">
          <Avatar person={t.assignee} ich={t.assignee !== null && t.assignee.id === p.ichId} />
        </span>
        <span className="tk__neben">{tageZwischen(berlinerTag(t.createdAt), p.heute)} T</span>
        {t.commentCount > 0 && (
          <span className="tk__neben">{t.commentCount === 1 ? "1 Kommentar" : `${t.commentCount} Kommentare`}</span>
        )}
        {t.subtaskTotal > 0 && <span className="tk__neben">{t.subtaskDone}/{t.subtaskTotal} Teilaufgaben</span>}
        {link !== null && (
          <span className="tk__link">
            {link.systemType === "URL" ? "Link" : `${SYSTEM[link.systemType]} ${link.externalKey}`}
            {link.remoteState !== null ? `: ${link.remoteState}` : ""}
            {t.linkCount > 1 ? ` +${t.linkCount - 1}` : ""}
          </span>
        )}
        {/* „Verschieben …“ als Symbolknopf rechts in der Fußzeile (Entwurf v001, Sichtprobe 3):
            der Name bleibt „<Nummer> verschieben“, das Sofort-Popup nennt ihn fürs Auge. */}
        <span ref={verschiebenPopup} className="tk__aktion tk__oben">
          <KiKnopf art="leise" className="tk__verschieben" name={`${t.ticketNumber} verschieben`}
            onClick={() => p.onVerschieben(t)}>
            <Icon name="arrows-up-down-left-right" size={14} />
          </KiKnopf>
        </span>
      </div>
    </article>
  );
}
